import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { WarehouseAPI } from '../services/api';
import packageJson from '../../package.json';

interface User {
  email: string;
  name: string;
  isAdmin?: boolean;
  role?: string;
  picture?: string;
}

interface GoogleLoginResult {
  ok: boolean;
  pending?: boolean;
  needsReason?: boolean;
  needsReauth?: boolean;
  message?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  loginWithGoogle: (credential: string, reason?: string) => Promise<GoogleLoginResult>;
  logout: () => void;
  isLoading: boolean;
  authError: string | null;
  clearAuthError: () => void;
  createUser: (email: string, name: string, password: string, role?: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// LocalStorage keys
const SESSION_KEY = 'bundleManagerUser';
// Build-version marker. We persist the build version the running client saw so
// anything that reads it stays accurate across releases. This marker NO LONGER
// triggers a logout: a routine app-version bump keeps the user's session intact so
// updates are seamless. The ONLY intentional force-logout is the server-side
// TOKEN_EPOCH lever (backend/config/tokenEpoch.js), bumped manually when a release
// genuinely requires re-login, plus normal token expiry / a real 401.
const APP_VERSION_KEY = 'salty_appVersion';
const CURRENT_APP_VERSION = packageJson.version;

// Refresh the client-side build-version marker on load. This used to clear a stale
// session on a version bump, but that logged everyone out on every routine release;
// it now only keeps APP_VERSION_KEY current and never removes auth_token or the
// session. Force-logout is owned solely by the server-side TOKEN_EPOCH mechanism
// (plus normal token expiry / a genuine 401).
function refreshAppVersionMarker(): void {
  try {
    window.localStorage.setItem(APP_VERSION_KEY, CURRENT_APP_VERSION);
  } catch {
    // Storage unavailable (Safari private mode / disabled) — nothing to do; the
    // server-side epoch check still force-logs-out stale tokens when needed.
  }
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  // Check for existing session on app load
  useEffect(() => {
    // Keep the build-version marker current. A version bump no longer logs anyone
    // out — the stored session is left untouched below so routine updates are seamless.
    refreshAppVersionMarker();

    const savedUser = window.localStorage.getItem(SESSION_KEY);
    if (savedUser) {
      try {
        const parsedUser = JSON.parse(savedUser);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing saved user:', error);
        window.localStorage.removeItem(SESSION_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setAuthError(null);
    try {
      const url = `${WarehouseAPI.UNICOMMERCE_PROXY_BASE}/api/auth/login`;
      const res = await axios.post(url, { email, password });
        if (res.data && res.data.success && res.data.data) {
          const u = res.data.data;
          const userData: User = { email: u.email, name: u.name, isAdmin: !!u.isAdmin, role: u.role };
        setUser(userData);
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
        if (res.data.token) {
          window.localStorage.setItem('auth_token', res.data.token);
        }
        setIsLoading(false);
        setAuthError(null);
        return true;
      }
      setAuthError(res.data?.message || 'Invalid credentials');
      setIsLoading(false);
      return false;
    } catch (err) {
      console.error('Login error:', err);
      const error = err as any;
      setAuthError(error?.response?.data?.message || 'Login failed. Please try again.');
      setIsLoading(false);
      return false;
    }
  };

  // loginWithGoogle: exchange a Google ID token credential for a session. The backend
  // (POST /api/auth/google) decides everything from the verified token; we only forward
  // the credential and an optional reason, and act on the returned flags.
  const loginWithGoogle = async (credential: string, reason?: string): Promise<GoogleLoginResult> => {
    // NOTE: deliberately does NOT touch the global `isLoading`. ProtectedRoute unmounts
    // LoginPage while isLoading is true; if the Google flow flipped it, the needsReason /
    // pending result would be set on a LoginPage instance that then gets remounted with fresh
    // state — so the reason dialog / pending panel never appeared and the user bounced back to
    // a blank login. LoginPage owns its own local busy state for the Google flow instead.
    setAuthError(null);
    try {
      const url = `${WarehouseAPI.UNICOMMERCE_PROXY_BASE}/api/auth/google`;
      const res = await axios.post(url, { credential, reason });
      const data = res.data || {};

      // Pending access request — no account yet, no token/redirect.
      if (data.pending) {
        return { ok: false, pending: true, message: data.message };
      }

      // Successful sign-in with a token.
      if (data.success && data.data && res.data.token) {
        const u = data.data;
        const userData: User = {
          email: u.email,
          name: u.name,
          isAdmin: !!u.isAdmin,
          role: u.role,
          picture: u.picture || undefined,
        };
        window.localStorage.setItem(SESSION_KEY, JSON.stringify(userData));
        window.localStorage.setItem('auth_token', res.data.token);
        setAuthError(null);
        // Set the user LAST — this flips ProtectedRoute into the app.
        setUser(userData);
        return { ok: true };
      }

      // Fallback: unexpected success-shaped response without a token.
      setAuthError(data.message || 'Google sign-in failed. Please try again.');
      return { ok: false, message: data.message };
    } catch (err) {
      const error = err as any;
      const data = error?.response?.data || {};

      // A reason is required to request access (unknown Salty account).
      if (data.needsReason) {
        return { ok: false, needsReason: true, message: data.message };
      }
      // The Google credential expired on resubmit — re-run the Google popup.
      if (data.needsReauth) {
        return { ok: false, needsReauth: true, message: data.message };
      }

      const message = data.message || 'Google sign-in failed. Please try again.';
      setAuthError(message);
      return { ok: false, message };
    }
  };

  const logout = () => {
    setUser(null);
    window.localStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem('auth_token');
    setAuthError(null);
  };

  // createUser: only callable by logged-in admin (frontend will send JWT)
  const createUser = async (email: string, name: string, password: string, role?: string): Promise<boolean> => {
    try {
      const url = `${WarehouseAPI.UNICOMMERCE_PROXY_BASE}/api/auth/create`;
      const token = window.localStorage.getItem('auth_token');
      const headers: any = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await axios.post(url, { email, name, password, role }, { headers });
      if (res.data && res.data.success) return true;
      throw new Error(res.data?.message || 'Create user failed');
    } catch (err) {
      console.error('Create user error:', err);
      const error = err as any;
      setAuthError(error?.response?.data?.message || 'Create user failed. Please try again.');
      setIsLoading(false);
      return false;
    }
  };
  const value: AuthContextType = {
    user,
    login,
    loginWithGoogle,
    logout,
    isLoading,
    authError,
    clearAuthError: () => setAuthError(null),
    createUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};